
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true, 
  redirectTo 
}) => {
  const location = useLocation();
  
  // Safely use auth context with error boundary
  let user, session, loading;
  try {
    const authContext = useAuth();
    user = authContext.user;
    session = authContext.session;
    loading = authContext.loading;
  } catch (error) {
    console.error('AuthGuard: Error accessing auth context:', error);
    // If auth context fails, treat as not authenticated
    user = null;
    session = null;
    loading = false;
  }

  console.log('AuthGuard check:', { 
    user: !!user, 
    session: !!session, 
    loading, 
    requireAuth, 
    pathname: location.pathname,
    redirectTo
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Loading...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // For routes that require authentication
  if (requireAuth && (!user || !session)) {
    console.log('Redirecting to login, user not authenticated');
    const defaultRedirect = '/login';
    return <Navigate to={redirectTo || defaultRedirect} state={{ from: location }} replace />;
  }

  // For routes that should only be accessible to non-authenticated users
  if (!requireAuth && user && session) {
    console.log('User is authenticated, redirecting');
    const defaultRedirect = '/app';
    const from = location.state?.from?.pathname || defaultRedirect;
    return <Navigate to={redirectTo || from} replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
